import PropTypes from "prop-types";
import React, { useEffect, useRef, useCallback, useState } from "react";
import SimpleBar from "simplebar-react";
import MetisMenu from "metismenujs";
import { Link, useLocation } from "react-router-dom";
import withRouter from "../Common/withRouter";
import { withTranslation } from "react-i18next";
import { useRoleContext, ROLE_PM, ROLE_TENANT } from "../../context/RoleContext";
import { useAdmin } from "../../hooks/useAdmin";
import { useAuthUser } from "../../hooks/AuthProvider";
import { onUnreadListingInquiriesForPm } from "../../helpers/firestoreService";

const SidebarContent = (props) => {
  const ref = useRef();
  const path = useLocation();
  const { activeRole, tenantRecords, setActiveRole } = useRoleContext();
  const { isAdmin } = useAdmin();
  const user = useAuthUser();
  const [unreadListingInquiries, setUnreadListingInquiries] = useState(0);

  useEffect(() => {
    if (activeRole !== ROLE_PM || !user?.uid) {
      setUnreadListingInquiries(0);
      return undefined;
    }
    const unsubscribe = onUnreadListingInquiriesForPm(user.uid, setUnreadListingInquiries);
    return () => unsubscribe();
  }, [activeRole, user?.uid]);

  const activateParentDropdown = useCallback((item) => {
    item.classList.add("active");
    const parent = item.parentElement;
    const parent2El = parent.childNodes[1];
    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show");
    }

    if (parent) {
      parent.classList.add("mm-active");
      const parent2 = parent.parentElement;

      if (parent2) {
        parent2.classList.add("mm-show");

        const parent3 = parent2.parentElement;

        if (parent3) {
          parent3.classList.add("mm-active");
          parent3.childNodes[0].classList.add("mm-active");
          const parent4 = parent3.parentElement;
          if (parent4) {
            parent4.classList.add("mm-show");
            const parent5 = parent4.parentElement;
            if (parent5) {
              parent5.classList.add("mm-show");
              parent5.childNodes[0].classList.add("mm-active");
            }
          }
        }
      }
      scrollElement(item);
      return false;
    }
    scrollElement(item);
    return false;
  }, []);

  const removeActivation = (items) => {
    for (let i = 0; i < items.length; ++i) {
      const item = items[i];
      const parent = item.parentElement;

      if (item && item.classList.contains("active")) {
        item.classList.remove("active");
      }
      if (parent) {
        const parent2El =
          parent.childNodes &&
          parent.childNodes.length &&
          parent.childNodes[1]
            ? parent.childNodes[1]
            : null;

        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show");
        }

        parent.classList.remove("mm-active");
        const parent2 = parent.parentElement;

        if (parent2) {
          parent2.classList.remove("mm-show");

          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.remove("mm-active");
            parent3.childNodes[0].classList.remove("mm-active");

            const parent4 = parent3.parentElement;
            if (parent4) {
              parent4.classList.remove("mm-show");
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.remove("mm-show");
                parent5.childNodes[0].classList.remove("mm-active");
              }
            }
          }
        }
      }
    }
  };

  const activeMenu = useCallback(() => {
    const pathName = path.pathname;
    let matchingMenuItem = null;
    const ul = document.getElementById("side-menu");
    const items = ul.getElementsByTagName("a");
  
    removeActivation(items);
  
    for (let i = 0; i < items.length; ++i) {
      const itemPath = new URL(items[i].href, window.location.origin).pathname;
      if (itemPath === pathName) {
        matchingMenuItem = items[i];
        break;
      }
    }
  
    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    }
  }, [path.pathname, activateParentDropdown]);
  

  useEffect(() => {
    ref.current?.recalculate();
  }, []);

  useEffect(() => {
    new MetisMenu("#side-menu");
    activeMenu();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    activeMenu();
  }, [activeMenu]);

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            <li className="menu-title">{props.t("Menu")}</li>

            {/* PM nav */}
            {activeRole === ROLE_PM && (
              <>
                <li>
                  <Link to="/overview">
                    <i className="bx bx-grid-alt"></i>
                    <span>{props.t("Overview")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/properties">
                    <i className="bx bx-building-house"></i>
                    <span>{props.t("Properties")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/applications">
                    <i className="bx bx-file"></i>
                    <span>{props.t("Applications")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/listings">
                    <i className="bx bx-message-square-dots"></i>
                    <span>{props.t("Listing Inquiries")}</span>
                    {unreadListingInquiries > 0 && (
                      <span className="badge rounded-pill bg-danger float-end">{unreadListingInquiries}</span>
                    )}
                  </Link>
                </li>
                <li>
                  <Link to="/tenants">
                    <i className="bx bx-buildings"></i>
                    <span>{props.t("Tenants")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/payments">
                    <i className="bx bx-credit-card"></i>
                    <span>{props.t("Payments")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/inspections">
                    <i className="bx bx-clipboard"></i>
                    <span>{props.t("Inspections")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/chats">
                    <i className="bx bx-conversation"></i>
                    <span>{props.t("Chats")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/tickets">
                    <i className="bx bx-task"></i>
                    <span>{props.t("Tickets")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/audit">
                    <i className="bx bx-history"></i>
                    <span>{props.t("Audit Log")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/tenant/home" onClick={() => setActiveRole(ROLE_TENANT)}>
                    <i className="bx bx-arrow-back"></i>
                    <span>{props.t("Tenant Mode")}</span>
                  </Link>
                </li>
              </>
            )}

            {/* Guest nav — shown when not logged in */}
            {!activeRole && (
              <>
                <li>
                  <Link to="/overview" onClick={() => setActiveRole(ROLE_PM)}>
                    <i className="bx bx-building"></i>
                    <span>{props.t("Property Manager")}</span>
                  </Link>
                </li>
              </>
            )}

            {/* Tenant nav */}
            {activeRole === ROLE_TENANT && (
              <>
                <li>
                  <Link to="/tenant/home">
                    <i className="bx bx-home-alt"></i>
                    <span>{props.t("Home")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/tenant/my-rentals">
                    <i className="bx bx-buildings"></i>
                    <span>{props.t("My Rentals")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/my-applications">
                    <i className="bx bx-file"></i>
                    <span>{props.t("My Applications")}</span>
                  </Link>
                </li>
                <li>
                  <Link to="/overview" onClick={() => setActiveRole(ROLE_PM)}>
                    <i className="bx bx-arrow-back"></i>
                    <span>{props.t("Property Manager Mode")}</span>
                  </Link>
                </li>
              </>
            )}

            {/* Admin nav */}
            {isAdmin && (
              <>
                <li className="menu-title mt-2">Admin</li>
                <li>
                  <Link to="/admin/users">
                    <i className="bx bx-user-check"></i>
                    <span>Manage Users</span>
                  </Link>
                </li>
                <li>
                  <Link to="/admin/contact">
                    <i className="bx bx-envelope"></i>
                    <span>Contact Messages</span>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(SidebarContent));
